import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useChatDepartments } from '../hooks/use-chat-departments';
import { useStartChat } from '../hooks/use-start-chat';
import { useInboxScope } from '../lib/inbox-scope';
import {
  emptyStartChatForm,
  startChatFormSchema,
  toStartChat,
  type StartChatForm,
} from '../schemas/start-chat-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['body', 'departmentId']);
/** Radix Select items cannot have an empty value. */
const DEFAULT_DEPARTMENT = '__default';

export interface StartChatTarget {
  storeId: string;
  visitorId: string;
  /** "Maya Chen" or "Visitor 4F2A". */
  name: string;
}

/** The visitor already chatting: the API's 409 names their open conversation. */
function existingConversationOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const id = error.meta.conversationId;
  return typeof id === 'string' ? id : null;
}

function StartChatBody({ target, onClose }: { target: StartChatTarget; onClose: () => void }) {
  const { t } = useTranslation(['conversations', 'common']);
  const message = useErrorMessage();
  const navigate = useNavigate();
  const scope = useInboxScope();
  const departments = useChatDepartments(target.storeId);
  const start = useStartChat(target.storeId, target.visitorId);
  const [existing, setExisting] = useState<string | null>(null);
  const form = useForm<StartChatForm>({
    resolver: useI18nResolver(startChatFormSchema),
    defaultValues: emptyStartChatForm(),
  });

  const openThread = (conversationId: string) => {
    onClose();
    void navigate(scope.threadLink({ id: conversationId, storeId: target.storeId }));
  };

  const submit = form.handleSubmit(async (values) => {
    setExisting(null);
    try {
      const { conversation } = await start.mutateAsync(toStartChat(values));
      openThread(conversation.id);
    } catch (error) {
      const conversationId = existingConversationOf(error);
      if (conversationId) {
        setExisting(conversationId);
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const requestId = error instanceof ApiError ? error.requestId : undefined;
      form.setError('root.server', {
        type: 'server',
        message: requestId
          ? `${message(error)} (${t('errors:requestIdShort', { requestId })})`
          : message(error),
      });
    }
  });

  const options = departments.data ?? [];
  const serverError = form.formState.errors.root?.server?.message;

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {existing ? (
          <Alert role="alert">
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>{t('conversations:startChat.alreadyChatting', { name: target.name })}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => openThread(existing)}
              >
                {t('conversations:startChat.openExisting')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
        {serverError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('conversations:startChat.message')}</FormLabel>
              <FormControl>
                <Textarea
                  autoFocus
                  rows={4}
                  placeholder={t('conversations:startChat.placeholder')}
                  {...field}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                      event.preventDefault();
                      void submit();
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {options.length > 0 ? (
          <FormField
            control={form.control}
            name="departmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('conversations:startChat.department')}</FormLabel>
                <Select
                  value={field.value || DEFAULT_DEPARTMENT}
                  onValueChange={(value) =>
                    field.onChange(value === DEFAULT_DEPARTMENT ? '' : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger className="w-full" onBlur={field.onBlur}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={DEFAULT_DEPARTMENT}>
                      {t('conversations:startChat.defaultDepartment')}
                    </SelectItem>
                    {options.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={start.isPending}>
            {start.isPending
              ? t('conversations:startChat.sending')
              : t('conversations:startChat.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * tawk.to's "start chat" from the visitor list: the first message (and a department), then the
 * new thread opens. The shopper's widget opens on it (LC7a).
 */
export function StartChatDialog({
  target,
  onOpenChange,
}: {
  target: StartChatTarget | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('conversations');
  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('startChat.title', { name: target?.name ?? '' })}</DialogTitle>
          <DialogDescription>{t('startChat.description')}</DialogDescription>
        </DialogHeader>
        {target ? (
          <StartChatBody
            key={`${target.storeId}:${target.visitorId}`}
            target={target}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
